/**
 * acquireMicrophone — the ONE microphone-acquisition path in the task pane.
 *
 * Both voice affordances (the composer microphone and the top-of-pane
 * hands-free entry) call this and nothing else. A second acquisition path is
 * how AC-6 came to hold for one half of the pane and not the other: the
 * hands-free leg ran `checkVoiceCapability({probePermission: true})`, a
 * getUserMedia-FIRST probe that never asks Office for the device — which is
 * precisely the diagnosed new-Outlook silent failure.
 *
 * The contract, in order:
 *   (a) resolve the host from the supported signal — for LOGGING only;
 *   (b) ask Office for the microphone through `requestOfficeMicrophonePermission()`
 *       when a device-permission object exists;
 *   (c) open a real stream through `probeMicrophoneStream()`;
 *   (d) the CALLER renders "requesting microphone" synchronously on the same
 *       interaction (this module is async by nature — it must never be awaited
 *       before the pending state is shown);
 *   (e) resolve inside a 10-second budget to `available`, `granted_pending_reload`
 *       or `degraded` — never hanging, never throwing.
 *
 * THE AVAILABILITY LOGIC (panel-fix-4 — the earlier text failed classic Outlook
 * and never re-probed after a first grant). Three rules, verified against
 * `probeHostCapability.ts` and @types/office-js:
 *
 *  1. CAPABILITY-FIRST, NEVER HOST-STRING-FIRST. Try the Office API when the
 *     object is present; when it is ABSENT — `requestOfficeMicrophonePermission`
 *     returns `status: "unavailable"` with "no device-permission object on this
 *     host — nothing to call" (`probeHostCapability.ts:469-475`) — FALL THROUGH
 *     to `getUserMedia`. That fall-through is the classic-Outlook SUCCESS path:
 *     DevicePermission 1.1 is web / new-Outlook only (`:32-36`), and classic
 *     Outlook shows a NATIVE prompt with no developer work. "API absent →
 *     degraded" would stop classic ever reaching `getUserMedia` and fail AC-5.
 *     Nothing here branches on `diagnostics.hostName` (it is `undefined` under
 *     ReadItem and the PRD forbids it); the host string is recorded for LOGGING.
 *
 *  2. FIRST GRANT REQUIRES A RE-PROBE. `asyncResult.value === true` means
 *     "granted for the FIRST time — the frame must RELOAD before the webview
 *     honours it" (`probeHostCapability.ts:543-551`, quoted verbatim). Treating
 *     it as `available` makes the attempt fail on exactly the turn the user
 *     granted permission. So a first grant re-probes the browser layer inside
 *     the remaining budget: some hosts honour it at once (→ `available`), and
 *     when the webview does not, the verdict is `granted_pending_reload` and
 *     the pane says so rather than pretending the mic is live.
 *
 *  3. THE OFFICE CALL IS BOUND AT 10s — deliberately NOT
 *     `OFFICE_CALLBACK_TIMEOUT_MS` (`probeHostCapability.ts:446`, 180_000).
 *     Reusing that constant would make AC-6's ten-second resolution false by
 *     three minutes. Because the host's own modal may still be open when the
 *     budget expires, a timeout carries `hostDialogPending` so the pane can hold
 *     a single non-auto-dismissing "still waiting on the host permission dialog"
 *     state instead of silently abandoning a grant in flight. A grant that
 *     arrives after the fallback needs a re-tap (or rule 2's reload) — that is
 *     documented here rather than discovered in the field.
 *
 * Design rules inherited from the probe module: nothing throws, every path
 * returns a result, the mic is never held open, `Office` is read only through
 * guards (it is undefined under jsdom), and the microphone is requested only on
 * an explicit use of an affordance — never on pane load.
 */

import { logger } from "../../obs/logger";
import {
  probeHostIdentity,
  probeMicrophoneStream,
  requestOfficeMicrophonePermission,
  type ProbeRow,
} from "./probeHostCapability";

/**
 * `available` — a live microphone was opened and released; the caller may arm.
 * `granted_pending_reload` — Office granted for the first time and the webview
 *   does not honour it yet (rule 2). Not a failure, not usable this turn.
 * `degraded` — no microphone this turn; the caller steers to standard chat.
 */
export type MicAcquisitionStatus =
  | "available"
  | "granted_pending_reload"
  | "blocked"
  | "degraded";

export interface MicAcquisitionResult {
  status: MicAcquisitionStatus;
  /** Why, in one plain sentence. `null` only when `available`. */
  reason: string | null;
  /** Resolved host, attached to every log record (DoD Monitoring). */
  host: string;
  /** Ordered record of the stages that actually ran — diagnosable after ship. */
  stages: string[];
  /**
   * The host's permission modal may still be open: the budget expired while the
   * Office callback was outstanding. The pane holds a non-auto-dismissing
   * "still waiting" message rather than claiming a refusal (rule 3).
   */
  hostDialogPending: boolean;
}

/**
 * AC-6's bound. Ten seconds, not three — acquisition genuinely takes seconds
 * even in classic Outlook — and emphatically not the source helper's 180s.
 */
export const MIC_ACQUISITION_BUDGET_MS = 10_000;

/** Reasons, as the pane states them. The steer sentence itself is shared copy. */
export const MIC_REASONS = {
  hostDialogPending:
    "Still waiting on the host's microphone permission dialog.",
  grantedPendingReload:
    "Microphone access was just granted — the pane has to reload before it can use it.",
  noMicrophoneApi: "This Outlook webview offers no microphone to the pane.",
  blocked: "Microphone access is blocked for this Outlook pane.",
  noDevice: "No microphone is available to this Outlook pane.",
  deviceBusy: "The microphone is in use by another application.",
  unexpected: "The microphone could not be reached.",
} as const;

/**
 * Turn a `getUserMedia` failure into a sentence a user can act on.
 *
 * The raw `DOMException.name: message` still reaches the log line (and the
 * diagnostics panel renders it verbatim) — but "NotAllowedError: Permission
 * denied" in the task pane is the silent-breakage defect wearing a different
 * hat. The names are the standard media-capture set; anything unrecognised
 * falls back to the probe's own summary rather than being swallowed.
 */
function humaniseMicFailure(summary: string): string {
  if (/^NotAllowedError|SecurityError/i.test(summary)) return MIC_REASONS.blocked;
  if (/^NotFoundError|OverconstrainedError/i.test(summary)) return MIC_REASONS.noDevice;
  if (/^NotReadableError|AbortError/i.test(summary)) return MIC_REASONS.deviceBusy;
  return summary || MIC_REASONS.unexpected;
}

/* -------------------------------------------------------------------------- */
/* Host identity — logging only, never a branch                               */
/* -------------------------------------------------------------------------- */

/**
 * The host as one loggable string. Read through `probeHostIdentity()` so there
 * is one reader of the Office diagnostics in the app, and guarded so a jsdom
 * run (no `Office` at all) resolves to a plain token instead of throwing.
 */
export function resolveHostForLogging(): string {
  try {
    const row = probeHostIdentity();
    return row.status === "pass" ? row.summary : `unknown (${row.summary})`;
  } catch {
    return "unknown (host identity unreadable)";
  }
}

/* -------------------------------------------------------------------------- */
/* Budget                                                                     */
/* -------------------------------------------------------------------------- */

interface Budgeted<T> {
  timedOut: boolean;
  value: T | null;
}

/**
 * Race `work` against `ms`. `work` is always one of the probe helpers, which are
 * total by design — the `.catch` is belt-and-braces so no rejection can escape
 * as an unhandled one after the race has already settled (AC-6's "no unhandled
 * permission rejection").
 */
async function withBudget<T>(work: Promise<T>, ms: number): Promise<Budgeted<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settled: Promise<Budgeted<T>> = work.then(
    (value) => ({ timedOut: false, value }),
    () => ({ timedOut: false, value: null }),
  );
  const expired = new Promise<Budgeted<T>>((resolve) => {
    timer = setTimeout(() => resolve({ timedOut: true, value: null }), ms);
  });
  try {
    return await Promise.race([settled, expired]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/* -------------------------------------------------------------------------- */
/* The acquisition                                                            */
/* -------------------------------------------------------------------------- */

/** `asyncResult.value === true` — the FIRST grant, per the .d.ts (rule 2). */
function isFirstGrant(row: ProbeRow): boolean {
  if (row.status !== "pass") return false;
  const detail = (row.details ?? []).find(
    ([key]) => key === "asyncResult.value" || key === "resolved value",
  );
  if (detail) return String(detail[1]).toLowerCase() === "true";
  return /granted for the first time/i.test(row.summary);
}

export async function acquireMicrophone(
  options: { budgetMs?: number; onRequesting?: () => void } = {},
): Promise<MicAcquisitionResult> {
  // Same turn as the press, before the first await (C46).
  options.onRequesting?.();
  const budgetMs = options.budgetMs ?? MIC_ACQUISITION_BUDGET_MS;
  const startedAt = Date.now();
  const remaining = (): number =>
    Math.max(0, budgetMs - (Date.now() - startedAt));
  const host = resolveHostForLogging();
  const stages: string[] = [];

  const degraded = (
    reason: string,
    extra: { hostDialogPending?: boolean; status?: "degraded" | "blocked" } = {},
  ): MicAcquisitionResult => {
    const result: MicAcquisitionResult = {
      status: extra.status ?? "degraded",
      reason,
      host,
      stages,
      hostDialogPending: extra.hostDialogPending ?? false,
    };
    // DoD Monitoring: every microphone-permission failure is logged WITH the
    // resolved host, so the unresolved new-Outlook defect stays diagnosable
    // after ship. No transcript text, no email content, no token.
    logger.warn("voice.microphone.unavailable", {
      phase: "voice",
      host,
      reason,
      stages: stages.join(" > "),
      host_dialog_pending: result.hostDialogPending,
    });
    return result;
  };

  try {
    // ── (b) Office device permission, when the object exists ────────────────
    const office = await withBudget(
      requestOfficeMicrophonePermission(),
      remaining(),
    );
    if (office.timedOut) {
      // Rule 3: the modal is plausibly still up. Say so; do not claim a refusal.
      stages.push("office:timed-out");
      return degraded(MIC_REASONS.hostDialogPending, { hostDialogPending: true });
    }
    const officeRow = office.value;
    if (officeRow) {
      stages.push(`office:${officeRow.status}`);
      if (isFirstGrant(officeRow)) {
        // Rule 2: granted for the FIRST time — re-probe rather than assume.
        stages.push("office:first-grant");
        const reprobe = await withBudget(probeMicrophoneStream(), remaining());
        if (!reprobe.timedOut && reprobe.value?.status === "pass") {
          stages.push("getusermedia:pass");
          return { status: "available", reason: null, host, stages, hostDialogPending: false };
        }
        stages.push(`getusermedia:${reprobe.timedOut ? "timed-out" : reprobe.value?.status}`);
        logger.warn("voice.microphone.granted_pending_reload", {
          phase: "voice",
          host,
          stages: stages.join(" > "),
        });
        return {
          status: "granted_pending_reload",
          reason: MIC_REASONS.grantedPendingReload,
          host,
          stages,
          hostDialogPending: false,
        };
      }
      // Rule 1: `unavailable` (no device-permission object on this host) and
      // even `fail` both FALL THROUGH — `getUserMedia` is the authority on
      // whether this pane can open a microphone, and on classic Outlook it is
      // the only one that will ever answer.
    }

    // ── (c) the browser layer ───────────────────────────────────────────────
    const media = await withBudget(probeMicrophoneStream(), remaining());
    if (media.timedOut) {
      stages.push("getusermedia:timed-out");
      return degraded(MIC_REASONS.hostDialogPending, { hostDialogPending: true });
    }
    const mediaRow = media.value;
    if (!mediaRow) {
      stages.push("getusermedia:no-result");
      return degraded(MIC_REASONS.unexpected);
    }
    stages.push(`getusermedia:${mediaRow.status}`);
    if (mediaRow.status === "pass") {
      return { status: "available", reason: null, host, stages, hostDialogPending: false };
    }
    // The raw DOMException still reaches the log through `stages` + the probe
    // row; what the pane SHOWS is a sentence the user can act on.
    logger.warn("voice.microphone.probe_failed", {
      phase: "voice",
      host,
      probe_summary: mediaRow.summary,
    });
    const shown =
      mediaRow.status === "unavailable"
        ? MIC_REASONS.noMicrophoneApi
        : humaniseMicFailure(mediaRow.summary);
    // A policy or host refusal is blocked, never a soft "degraded" denial.
    return degraded(shown, {
      status: shown === MIC_REASONS.blocked ? "blocked" : "degraded",
    });
  } catch (err) {
    // Nothing above is expected to throw; if a host ever makes one of the
    // guarded reads explode, the pane still gets a result and a log line.
    stages.push("threw");
    logger.error("voice.microphone.acquisition_threw", {
      phase: "voice",
      host,
      error: err instanceof Error ? err.message : String(err),
      stages: stages.join(" > "),
    });
    return {
      status: "degraded",
      reason: MIC_REASONS.unexpected,
      host,
      stages,
      hostDialogPending: false,
    };
  }
}

export default acquireMicrophone;
